import useAxios from "src/core/shared/hooks/useAxios";
import { IList } from "src/core/lists/api/lists.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";

export type CreateScheduleParams = Pick<
  ISchedule,
  "bankHolidaysDelivery" | "listId" | "scheduleDays" | "startDate" | "endDate" | "monthlyFrequency" | "status" | "validating" | "type"
>;
export type UpdateScheduleParams = Partial<ISchedule> & Pick<ISchedule, "id">;

export interface ISchedule {
  id?: number;
  bankHolidaysDelivery: number;
  createdAt?: Date;
  endDate: Date | null;
  listId: number;
  monthlyFrequency: ScheduleMonthlyFrequency;
  scheduleDays?: ScheduleDays[];
  startDate: Date;
  status: ScheduleStatus;
  updatedAt: Date;
  customerReference?: string;
  validating: boolean;
  type: ScheduleType;
}

export interface SchedulePreRequestUpdate {
  toCreate?: number;
  toCancel?: number;
  affectedOrders?: number;
  message?: string;
}

export enum ScheduleType {
  Manual = "manual",
  Ongoing = "ongoing",
}

export enum ScheduleStatus {
  Active = "active",
  Cancelled = "cancelled",
  Suspended = "suspended"
}

export type ScheduleMonthlyFrequency = 1 | 2 | 3 | 4;
export type ScheduleDays = 1 | 2 | 3 | 4 | 5 | 6 | 7;

type ScheduleEndpointConfig = {
  GET_SCHEDULE: EndpointConfig<
    { scheduleId: number},
    ISchedule
  >;
  CREATE_SCHEDULE: EndpointConfig<
    CreateScheduleParams,
    ISchedule
  >;
  PRE_UPDATE_SCHEDULE: EndpointConfig<
    { schedule: UpdateScheduleParams },
    SchedulePreRequestUpdate
  >;
  UPDATE_SCHEDULE: EndpointConfig<
    UpdateScheduleParams,
    ISchedule
  >;
  CANCEL_SCHEDULE: EndpointConfig<
    { scheduleId: number},
    ISchedule
  >;
  SUSPEND_SCHEDULE: EndpointConfig<
    { scheduleId: number},
    ISchedule
  >;
  REACTIVATE_SCHEDULE: EndpointConfig<
    { scheduleId: number},
    ISchedule
  >;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/schedules`;

const requests: RequestConfig<ScheduleEndpointConfig> = {
  GET_SCHEDULE: ({ scheduleId }) => ({
    url: `${BASE_URL}/${scheduleId}`,
    method: "GET"
  }),
  CREATE_SCHEDULE: data => ({
    url: `${BASE_URL}/`,
    method: "POST",
    data
  }),
  PRE_UPDATE_SCHEDULE: params => ({
    url: `${BASE_URL}/pre-update/info`,
    method: "GET",
    params
  }),
  UPDATE_SCHEDULE: data => ({
    url: `${BASE_URL}/`,
    method: "PATCH",
    data
  }),
  CANCEL_SCHEDULE: ({ scheduleId }) => ({
    url: `${BASE_URL}/${scheduleId}/cancel`,
    method: "PATCH"
  }),
  SUSPEND_SCHEDULE: ({ scheduleId }) => ({
    url: `${BASE_URL}/${scheduleId}/suspend`,
    method: "PATCH"
  }),
  REACTIVATE_SCHEDULE: ({ scheduleId }) => ({
    url: `${BASE_URL}/${scheduleId}/reactivate`,
    method: "PATCH"
  })
};

export const useScheduleApi: UseApiRequest<ScheduleEndpointConfig> = name => useAxios(requests[ name ], name);